/* eslint-disable @typescript-eslint/unbound-method */
import BinanceEthereumProvider from '@binance/w3w-ethereum-provider'
import { ErrorResponse, IWCEthRpcConnectionOptions } from '@binance/w3w-types'
import { isInBinance } from '@binance/w3w-utils'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'

import { InjectedConnector } from './InjectedConnector'

const __DEV__: boolean = process.env.NODE_ENV !== 'production'

export const URI_AVAILABLE = 'URI_AVAILABLE'

export interface BinanceW3WConnectorArguments
  extends IWCEthRpcConnectionOptions {
  supportedChainIds?: number[]
}

export class UserRejectedRequestError extends Error {
  public constructor() {
    super()
    this.name = this.constructor.name
    this.message = 'The user rejected the request.'
  }
}

function getSupportedChains({
  supportedChainIds,
  rpc,
}: BinanceW3WConnectorArguments): number[] | undefined {
  if (supportedChainIds) {
    return supportedChainIds
  }

  return rpc ? Object.keys(rpc).map((k) => Number(k)) : undefined
}

class BinanceW3WWeb3Connector extends AbstractConnector {
  public binanceW3WProvider?: BinanceEthereumProvider
  private readonly config: BinanceW3WConnectorArguments

  constructor(config: BinanceW3WConnectorArguments) {
    super({ supportedChainIds: getSupportedChains(config) })
    this.config = config

    this.handleChainChanged = this.handleChainChanged.bind(this)
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this)
    this.handleDisconnect = this.handleDisconnect.bind(this)
  }

  private handleChainChanged(chainId: number | string): void {
    if (__DEV__) {
      console.log("Handling 'chainChanged' event with payload", chainId)
    }
    this.emitUpdate({ chainId })
  }

  private handleAccountsChanged(accounts: string[]): void {
    if (__DEV__) {
      console.log("Handling 'accountsChanged' event with payload", accounts)
    }
    this.emitUpdate({ account: accounts[0] })
  }

  private handleDisconnect(): void {
    if (__DEV__) {
      console.log("Handling 'disconnect' event")
    }
    if (this.binanceW3WProvider) {
      this.binanceW3WProvider.removeListener(
        'chainChanged',
        this.handleChainChanged
      )
      this.binanceW3WProvider.removeListener(
        'accountsChanged',
        this.handleAccountsChanged
      )
      this.binanceW3WProvider = undefined
    }
    this.emitDeactivate()
  }

  public async activate(): Promise<ConnectorUpdate> {
    if (!this.binanceW3WProvider) {
      const BinanceEthereumProvider = (
        await import('@binance/w3w-ethereum-provider')
      ).default

      this.binanceW3WProvider = new BinanceEthereumProvider(this.config)
    }
    const account = await new Promise<string>((resolve, reject) => {
      const userReject = () => {
        // Erase the provider manually
        this.binanceW3WProvider = undefined
        reject(new UserRejectedRequestError())
      }

      // Workaround to bubble up the error when user reject the connection
      this.binanceW3WProvider.connector.on('disconnect', () => {
        // Check provider has not been enabled to prevent this event callback from being called in the future
        if (!account) {
          userReject()
        }
      })

      if (this.config.lng) this.binanceW3WProvider.setLng(this.config.lng)
      this.binanceW3WProvider
        .enable(this.config.chainId)
        .then((accounts: string[]) => resolve(accounts[0]))
        .catch((error: ErrorResponse): void => {
          if (error.code === 100001) {
            userReject()
            return
          }
          reject(error)
        })
    }).catch((err) => {
      throw err
    })

    this.binanceW3WProvider.on('disconnect', this.handleDisconnect)
    this.binanceW3WProvider.on('chainChanged', this.handleChainChanged)
    this.binanceW3WProvider.on('accountsChanged', this.handleAccountsChanged)

    return { provider: this.binanceW3WProvider, account }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async getProvider(): Promise<any> {
    return this.binanceW3WProvider
  }

  public async getChainId(): Promise<number | string> {
    return Promise.resolve(this.binanceW3WProvider.chainId)
  }

  public async getAccount(): Promise<null | string> {
    return Promise.resolve(this.binanceW3WProvider.accounts).then(
      (accounts: string[]): string => accounts[0]
    )
  }

  public deactivate() {
    if (this.binanceW3WProvider) {
      this.binanceW3WProvider.removeListener(
        'disconnect',
        this.handleDisconnect
      )
      this.binanceW3WProvider.removeListener(
        'chainChanged',
        this.handleChainChanged
      )
      this.binanceW3WProvider.removeListener(
        'accountsChanged',
        this.handleAccountsChanged
      )
      this.binanceW3WProvider.disconnect()
    }
  }

  public close() {
    this.emitDeactivate()
  }
}

export const getWeb3Connector = () => {
  if (isInBinance()) {
    return InjectedConnector
  }
  return BinanceW3WWeb3Connector
}
